import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchVariantsbyProductID,
  addInventoryStock,
  getStockValuePerVariant,
  createStockRequest,
} from "../../redux/Inventory/inventoryActions";
import { getAllStoreLocations } from "../../redux/StoreManagement/storeManagementActions";
import DrawerComponent from "../../components/Drawers/drawers";
import { Assignment } from "@mui/icons-material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import BoxAddIcon from "../../assets/icons/box_add.svg";
import go_back from "../../assets/icons/goBack.svg";
import toast from "react-hot-toast";
import { hasPermission } from "../../utils/permissionChecker";
import { PRIVILEGES } from "../../utils/privileges";

const AddInventoryDrawer = ({
  showDrawer,
  setShowDrawer,
  productId,
  variantId,
  onStockAdded,
  activeTab,
  setStockAdded,
  product,
}) => {
  const dispatch = useDispatch();
  const { addStockLoading, totalCurrentQuantity, createStockRequestLoading } =
    useSelector((state) => state.inventory);
  const { user } = useSelector((state) => state.auth);
  const { allStoreLocations, allStoreLocationsLoading } = useSelector(
    (state) => state.storeManagement
  );
  const [currentVariant, setCurrentVariant] = useState(null);
  const [addStockMode, setAddStockMode] = useState("");
  const [inventoryData, setInventoryData] = useState({
    costPrice: "",
    marketPrice: "",
    sellingPrice: "",
    profit: "",
    margin: "",
    quantity: "",
    expiryDate: "",
    SKU: "",
    storeId: "",
    productThreshold: "",
  });

  const [stockRequestData, setStockRequestData] = useState({
    requestQuantity: "",
    requestNote: "",
    storeId: "",
  });

  // Add these to your component state
  const [isToggleSelect, setToggleSelect] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  // Add request store state
  const [isRequestStoreToggle, setRequestStoreToggle] = useState(false);
  const [selectedRequestStore, setSelectedRequestStore] = useState(null);
  const [requestStoreSearch, setRequestStoreSearch] = useState("");

  // Filter only active stores
  const availableStores = (allStoreLocations || []).filter(
    (store) => store.is_active === true
  );

  // Filter stores based on search
  const filteredStores = availableStores.filter((store) =>
    store.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  // Filter request stores based on search
  const filteredRequestStores = availableStores.filter((store) =>
    store.name.toLowerCase().includes(requestStoreSearch.toLowerCase())
  );

  // Handle drawer opening and product data fetching
  useEffect(() => {
    if (showDrawer && productId) {
      // Fetch all store locations when drawer opens
      if (
        hasPermission(user, PRIVILEGES.REQUEST_NEW_STOCK) &&
        user.department !== "STORE"
      ) {
        dispatch(getAllStoreLocations());
      }

      dispatch(fetchVariantsbyProductID({ productId: productId }))
        .then((res) => {
          if (res.type === "fetchVariantsbyProductID/fulfilled") {
            const productData = res.payload.data;

            if (
              variantId &&
              productData.variants &&
              productData.variants.length > 0
            ) {
              const foundVariant = productData.variants.find(
                (variant) => String(variant.id) === String(variantId)
              );
              if (foundVariant) {
                setCurrentVariant(foundVariant);
                setInventoryData((prev) => ({
                  ...prev,
                  SKU: foundVariant.sku || "",
                }));
              }
            }
          } else if (res.type === "fetchVariantsbyProductID/rejected") {
            console.error("Failed to load product details:", res.payload);
          }
        })
        .catch((error) => {
          console.error("Error fetching product details:", error);
        });
    }
  }, [dispatch, showDrawer, productId, variantId, user]);

  // Handle stock value fetching - only when variantId changes
  useEffect(() => {
    if (variantId) {
      dispatch(getStockValuePerVariant(variantId));
    }
  }, [dispatch, variantId]);

  // Function to calculate profit and margin
  const calculateProfitAndMargin = (sellingPrice, costPrice) => {
    const profit = sellingPrice - costPrice;
    const margin = sellingPrice > 0 ? (profit / sellingPrice) * 100 : 0;
    return {
      profit: profit,
      margin: margin.toFixed(2),
      status: profit >= 0 ? "positive" : "negative",
    };
  };

  const handleInputChange = (field, value) => {
    if (field === "storeId") {
      setInventoryData((prev) => ({
        ...prev,
        storeId: value,
      }));
      return; // Early return to avoid double state update
    }

    setInventoryData((prev) => {
      const updated = { ...prev, [field]: parseFloat(value) || 0 };

      // Auto-calculate profit and margin only when selling price is entered
      if (field === "costPrice" || field === "sellingPrice") {
        const cost =
          field === "costPrice" ? parseFloat(value) || 0 : updated.costPrice;
        const selling =
          field === "sellingPrice"
            ? parseFloat(value) || 0
            : updated.sellingPrice;

        // Only calculate when selling price is greater than 0
        if (selling > 0) {
          const calculation = calculateProfitAndMargin(selling, cost);
          updated.profit = calculation.profit;
          updated.margin = calculation.margin;
        } else {
          // Reset to 0 when no selling price
          updated.profit = 0;
          updated.margin = 0;
        }
      }

      return updated;
    });
  };

  const isFormValid = () => {
    const quantity = parseFloat(inventoryData.quantity);
    const costPrice = parseFloat(inventoryData.costPrice);
    const sellingPrice = parseFloat(inventoryData.sellingPrice);
    const marketPrice = parseFloat(inventoryData.marketPrice);
    const productThreshold = parseFloat(inventoryData.productThreshold);

    return (
      quantity > 0 &&
      costPrice > 0 &&
      sellingPrice > 0 &&
      marketPrice > 0 &&
      productThreshold > 0 &&
      inventoryData.expiryDate.trim() !== ""
    );
  };

  const handleMakeRequest = async () => {
    const data = {
      variant_id: variantId,
      request_quantity: Number(stockRequestData.requestQuantity),
      store_id: selectedRequestStore?.id || null,
      note_body: stockRequestData.requestNote,
    };

    try {
      const result = await dispatch(createStockRequest(data));

      if (result.type === "createStockRequest/fulfilled") {
        // Clear form
        setStockRequestData({
          requestQuantity: "",
          requestNote: "",
          storeId: "",
        });
        setSelectedRequestStore(null);
        setRequestStoreSearch("");

        toast.success("Stock request created successfully!");
        setShowDrawer(false);

        if (onStockAdded) {
          onStockAdded("stock_request");
        }
      }
    } catch (error) {
      console.error("Error creating stock request:", error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!currentVariant) {
      alert("No variant selected");
      return;
    }

    // Validation
    if (!inventoryData.quantity || inventoryData.quantity <= 0) {
      alert("Please enter a valid quantity");
      return;
    }

    if (!inventoryData.costPrice || inventoryData.costPrice <= 0) {
      alert("Please enter a valid cost price");
      return;
    }

    if (!inventoryData.sellingPrice || inventoryData.sellingPrice <= 0) {
      alert("Please enter a valid selling price");
      return;
    }

    if (!inventoryData.marketPrice || inventoryData.marketPrice <= 0) {
      alert("Please enter a valid market price");
      return;
    }

    if (!inventoryData.productThreshold) {
      alert("Please enter a valid product threshold");
      return; // Added missing return
    }

    if (!inventoryData.expiryDate) {
      alert("Please enter a valid expiry date");
      return;
    }

    // Prepare payload for single variant
    const payload = [
      {
        variant_id: currentVariant.id,
        quantity: parseInt(inventoryData.quantity) || 0,
        cost_price: parseFloat(inventoryData.costPrice) || 0,
        selling_price: parseFloat(inventoryData.sellingPrice) || 0,
        market_price: parseFloat(inventoryData.marketPrice) || 0,
        sku: inventoryData.SKU || currentVariant.sku || "",
        expiry_date: inventoryData.expiryDate || null,
        store_id: inventoryData.storeId || null,
        reorder_point:
          parseInt(inventoryData.productThreshold?.replace(/[<>]/g, "")) || 10,
        notes: `Stock added for ${currentVariant.variant_data?.type} ${currentVariant.variant_data?.value}${currentVariant.variant_data?.unit}`,
      },
    ];

    console.log("Submitting stock data:", payload);

    dispatch(addInventoryStock({ variants_stock: payload }))
      .then((res) => {
        if (res.type === "addInventoryStock/fulfilled") {
          // Clear all inputs on success
          setInventoryData({
            costPrice: 0,
            marketPrice: 0,
            sellingPrice: 0,
            profit: 0,
            margin: 0,
            quantity: 0,
            expiryDate: "",
            SKU: "",
            productThreshold: "",
            storeId: "",
          });
          setShowDrawer(false);
          // Navigate to inventory tab after successful addition
          if (onStockAdded) {
            onStockAdded("manual_stock"); // Pass a parameter to indicate the type of action
            if (activeTab === "inventory") {
              setStockAdded(true);
            }
          }
        }
      })
      .catch((error) => {
        console.error("Error adding stock:", error);
      });
  };

  const handleStoreSelect = (store) => {
    setSelectedStore(store);
    setInventoryData((prev) => ({
      ...prev,
      storeId: store.id,
    }));
    setToggleSelect(false);
    setSearchValue("");
  };

  const handleRequestStoreSelect = (store) => {
    setSelectedRequestStore(store);
    setStockRequestData((prev) => ({
      ...prev,
      storeId: store.id,
    }));
    setRequestStoreToggle(false);
    setRequestStoreSearch("");
  };

  return (
    <DrawerComponent
      show={showDrawer}
      showDrawer={setShowDrawer}
      paddingAllowed={false}
      title={
        addStockMode === "manual" ? (
          <div className="flex flex-col gap-2">
            {/* Go back button */}
            <div
              className="flex items-center cursor-pointer"
              onClick={() => setAddStockMode("")}
            >
              <img src={go_back} alt="go back" className="w-[20px] h-[20px]" />
              <p className="text-sm font-semibold text-[#5C715E] ml-[4px] ">
                Go Back
              </p>
            </div>
            <span className=" font-semibold">Add Stock Manually</span>
          </div>
        ) : addStockMode === "request" ? (
          <div className="flex flex-col  gap-2">
            {/* Go back button */}
            <div
              className="flex items-center cursor-pointer"
              onClick={() => setAddStockMode("")}
            >
              <img src={go_back} alt="go back" className="w-[20px] h-[20px]" />
              <p className="text-sm font-semibold text-[#5C715E] ml-[4px] ">
                Go Back
              </p>
            </div>
            <span className=" font-semibold">Create Stock Request</span>
          </div>
        ) : (
          "Add Stock"
        )
      }
      subtitle={addStockMode === "" ? "Choose how you'd like to add stock" : ""}
    >
      {addStockMode === "" && (
        <div className="">
          <button
            onClick={() => setAddStockMode("manual")}
            className="pr-4 py-4 border-y border-[#E1E6E1] flex items-center justify-between gap-2 w-full hover:bg-[#F6F7F6] transition-colors "
          >
            <div className="flex items-center gap-2 px-6">
              <div className="bg-[#2B8C34] h-8 rounded-full w-8 flex items-center justify-center ">
                <Assignment className="text-white" />
              </div>
              <p className="text-[#3E473F] font-semibold text-sm">Manually</p>
            </div>
            <NavigateNextIcon className="text-xs text-[#5F6D60] px-6" />
          </button>
          <button
            onClick={() => setAddStockMode("request")}
            className="pr-4 py-4 border-b border-[#E1E6E1] flex items-center justify-between gap-2 w-full hover:bg-[#F6F7F6] transition-colors"
          >
            <div className="flex items-center gap-2 px-6">
              <div className="bg-[#2B8C34] h-8 rounded-full w-8 flex items-center justify-center">
                <img
                  src={BoxAddIcon}
                  alt="Add box"
                  className="h-5 w-5 text-white"
                />
              </div>
              <p className="text-[#3E473F] font-semibold text-sm">
                Stock Request
              </p>
            </div>
            <NavigateNextIcon className="text-xs text-[#5F6D60] px-6" />
          </button>
        </div>
      )}
      {addStockMode === "manual" && (
        <form
          onSubmit={handleSubmit}
          className="bg-white min-h-full overflow-y-auto"
        >
          {/* Toggle buttons for inventory type */}

          {/* Cost Price and Market Price Row */}
          <div className="bg-white pt-3 border-t border-[#E1E6E1] px-6">
            <div className="border border-[#E1E6E1] bg-[#F6F7F6] px-4 py-2 rounded-tl-[8px] rounded-tr-[8px] flex justify-between items-center">
              <p className="text-sm text-[#3E473F] font-semibold">Cost Price</p>
              <div className="w-fit px-0.5 text-center text-[#5F6D60] font-semibold text-xs border rounded-[4px]">
                Prev. CP ₦{currentVariant?.current_cost_price || "0"}
              </div>
            </div>
            <div className="px-4 py-3 border border-[#E1E6E1] bg-white rounded-bl-[8px] rounded-br-[8px]">
              <div className="flex items-center">
                <span className="text-[#5F6D60] font-medium text-xl pr-2">
                  ₦
                </span>
                <input
                  type="text"
                  value={inventoryData.costPrice || ""}
                  onChange={(e) =>
                    handleInputChange("costPrice", e.target.value)
                  }
                  className="flex-1 text-2xl font-semibold text-[#3E473F] bg-transparent border-none focus:outline-none"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          <div className="bg-white pt-4 px-6">
            <div className="border border-[#E1E6E1] bg-[#F6F7F6] px-4 py-2 rounded-tl-[8px] rounded-tr-[8px] ">
              <p className="text-sm text-[#3E473F] font-semibold">
                Market Price
              </p>
            </div>
            <div className="px-4 py-3 border border-[#E1E6E1] bg-white rounded-bl-[8px] rounded-br-[8px]">
              <div className="flex items-center">
                <span className="text-[#5F6D60] font-medium text-xl pr-2">
                  ₦
                </span>
                <input
                  type="text"
                  value={inventoryData.marketPrice || ""}
                  onChange={(e) =>
                    handleInputChange("marketPrice", e.target.value)
                  }
                  className="flex-1 text-2xl font-semibold text-[#3E473F] bg-transparent border-none focus:outline-none"
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>

          {/* Selling Price */}
          <div className="bg-white pt-4 px-6">
            <div className="border border-[#E1E6E1] bg-[#F6F7F6] px-4 py-2 rounded-tl-[8px] rounded-tr-[8px] flex justify-between items-center">
              <p className="text-sm text-[#3E473F] font-semibold">
                Selling Price
              </p>
              <div className="w-fit px-0.5 text-center text-[#5F6D60] font-semibold text-xs border rounded-[4px]">
                Prev. SP ₦{currentVariant?.current_selling_price || "0"}
              </div>
            </div>
            <div className="px-4 py-3 border border-[#E1E6E1] bg-white">
              <div className="flex items-center">
                <span className="text-[#5F6D60] font-medium text-xl pr-2">
                  ₦
                </span>
                <input
                  type="text"
                  value={inventoryData.sellingPrice || ""}
                  onChange={(e) =>
                    handleInputChange("sellingPrice", e.target.value)
                  }
                  className="flex-1 text-2xl font-semibold text-[#3E473F] bg-transparent border-none focus:outline-none"
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Profit Margin Details */}
            <div className="w-full flex border-t-0 border border-[#E1E6E1] rounded-bl-[8px] rounded-br-[8px]">
              <div
                className={`${
                  inventoryData.sellingPrice > 0
                    ? inventoryData.profit < 0
                      ? "bg-[#B92020]"
                      : "bg-[#2B8C34]"
                    : "bg-white"
                } flex rounded-bl-[8px] items-center py-2 px-4`}
                style={{ flex: "1.5" }}
              >
                <span
                  className={`${
                    inventoryData.sellingPrice > 0
                      ? "text-white"
                      : "text-[#5F6D60]"
                  } font-semibold text-sm`}
                >
                  {inventoryData.sellingPrice > 0 && inventoryData.profit < 0
                    ? "Loss"
                    : "Profit"}
                  :{" "}
                  <span
                    className={`${
                      inventoryData.sellingPrice > 0
                        ? "text-white"
                        : "text-[#96A397]"
                    }`}
                  >
                    ₦
                    {inventoryData.sellingPrice > 0
                      ? Math.abs(inventoryData.profit).toLocaleString()
                      : "0.00"}
                  </span>
                </span>
                <div
                  className={`h-4 w-px ${
                    inventoryData.sellingPrice > 0 ? "bg-white" : "bg-[#E1E6E1]"
                  } mx-3 opacity-50`}
                ></div>
                <span
                  className={`${
                    inventoryData.sellingPrice > 0
                      ? "text-white"
                      : "text-[#5F6D60]"
                  } font-semibold text-sm`}
                >
                  Margin:{" "}
                  <span
                    className={`${
                      inventoryData.sellingPrice > 0
                        ? "text-white"
                        : "text-[#96A397]"
                    }`}
                  >
                    {inventoryData.sellingPrice > 0
                      ? inventoryData.margin
                      : "0"}
                    %
                  </span>
                </span>
              </div>
              <div
                className={`${
                  inventoryData.sellingPrice > 0
                    ? inventoryData.profit < 0
                      ? "bg-[#FEF8D8] text-[#D8A600]"
                      : "bg-[#F2FBF3] text-[#25702C]"
                    : "bg-white text-white"
                } flex items-center justify-center rounded-br-[8px] text-sm font-semibold py-2 px-4 border-l border-[#E1E6E1]`}
                style={{ flex: "1" }}
              >
                {inventoryData.sellingPrice > 0 ? (
                  <span>
                    ₦
                    {Math.abs(
                      inventoryData.sellingPrice - inventoryData.marketPrice
                    ).toLocaleString()}{" "}
                    {inventoryData.sellingPrice > inventoryData.marketPrice
                      ? "Higher Than MP"
                      : "Lower Than MP"}
                  </span>
                ) : (
                  <span>&nbsp;</span>
                )}
              </div>
            </div>
          </div>

          {/* Quantity */}
          <div className="bg-white pt-4 px-6">
            <div className="border border-[#E1E6E1] bg-[#F6F7F6] px-4 py-2 rounded-tl-[8px] rounded-tr-[8px]">
              <p className="text-sm text-[#3E473F] font-semibold">Quantity</p>
            </div>
            <div className="px-4 py-3 border border-[#E1E6E1] bg-white rounded-bl-[8px] rounded-br-[8px]">
              <input
                type="text"
                value={inventoryData.quantity || ""}
                onChange={(e) => handleInputChange("quantity", e.target.value)}
                className="w-full text-2xl font-semibold text-[#3E473F] bg-transparent border-none focus:outline-none"
                placeholder="00"
              />
            </div>
          </div>

          {/* Select store */}
          {hasPermission(user, PRIVILEGES.ADD_STOCK_TO_STORE) && (
            <div className="pt-4 px-6">
              <div className="relative">
                <p className="text-sm font-semibold text-[#3E473F] mb-1">
                  Select Store
                </p>
                <div
                  className={`flex items-center cursor-pointer justify-between outline-0 h-[44px] bg-white rounded-lg border border-[#E1E6E1] px-3 w-full font-medium text-sm leading-5 ${
                    allStoreLocationsLoading
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                  onClick={() =>
                    !allStoreLocationsLoading &&
                    setToggleSelect(!isToggleSelect)
                  }
                >
                  {selectedStore ? (
                    <span className="font-semibold text-sm text-[#344335]">
                      {selectedStore.name}
                    </span>
                  ) : (
                    <span className="text-[#344335]">
                      {allStoreLocationsLoading
                        ? "Loading stores..."
                        : "Select store"}
                    </span>
                  )}
                  {isToggleSelect ? (
                    <svg
                      className="w-4 h-4 text-[#ABB6AC]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-4 h-4 text-[#ABB6AC]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  )}
                </div>
                {isToggleSelect && (
                  <div
                    className="absolute mt-2 border rounded-lg p-2 w-full bg-white z-20 max-h-[250px] overflow-y-scroll"
                    style={{
                      boxShadow:
                        "0px 16px 32px 0px #00000014, 0px 0px 1px 0px #00000040",
                    }}
                  >
                    <input
                      type="text"
                      placeholder="Search stores..."
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      className="w-full mb-2 px-2 py-2 border border-[#E1E6E1] rounded text-sm"
                    />
                    {filteredStores.length === 0 ? (
                      <div className="flex justify-center items-center h-[60px]">
                        <p className="text-[#96A397] text-sm">
                          No stores found
                        </p>
                      </div>
                    ) : (
                      filteredStores.map((store) => (
                        <div
                          onClick={() => handleStoreSelect(store)}
                          key={store.id}
                          className={`flex items-center gap-3 px-4 py-2 hover:bg-[#F6F7F6] hover:rounded cursor-pointer ${
                            selectedStore?.id === store.id
                              ? "bg-[#EDF7EE] rounded"
                              : ""
                          }`}
                        >
                          <div className="flex-1">
                            <div className="font-semibold text-sm text-[#3E473F]">
                              {store.name}
                            </div>
                            <div className="text-[#3E473F] text-xs font-medium">
                              {store.address || ""}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Inventory Info */}
          <div className="bg-white pt-4 px-6">
            <div className="border border-[#E1E6E1] bg-[#F6F7F6] px-4 py-2 rounded-tl-[8px] rounded-tr-[8px] flex justify-between items-center">
              <p className="text-sm text-[#3E473F] font-semibold">
                Inventory Info
              </p>
              <span className="text-xs text-[#5F6D60] bg-white border border-[#E1E6E1] rounded-[4px] py-1 px-2">
                {currentVariant?.sku || "No SKU"}
              </span>
            </div>
            <div className="px-4 py-3 border border-[#E1E6E1] bg-white rounded-bl-[8px] rounded-br-[8px]">
              <div className="grid grid-cols-2 gap-4">
                {/* Expiry Date */}
                <div>
                  <p className="text-sm text-[#3E473F] font-medium mb-2">
                    Expiry Date
                  </p>
                  <div className="border border-[#E1E6E1] w-full rounded-lg px-2">
                    <input
                      type="date"
                      min={new Date().toISOString().split("T")[0]}
                      value={inventoryData.expiryDate || ""}
                      onChange={(e) =>
                        setInventoryData((prev) => ({
                          ...prev,
                          expiryDate: e.target.value,
                        }))
                      }
                      className="w-full text-[#3E473F] text-sm font-semibold px-2 py-2 bg-transparent border-none focus:outline-none"
                    />
                  </div>
                </div>

                {/* Product Threshold */}
                <div>
                  <p className="text-sm text-[#3E473F] font-medium mb-2">
                    Product Threshold (Units)
                  </p>
                  <div className="border border-[#E1E6E1] w-full rounded-lg px-2 ">
                    <input
                      type="text"
                      value={inventoryData.productThreshold || ""}
                      onChange={(e) =>
                        setInventoryData((prev) => ({
                          ...prev,
                          productThreshold: e.target.value,
                        }))
                      }
                      className="w-full text-[#3E473F] text-sm font-semibold px-2 py-2 bg-transparent border-none focus:outline-none"
                      placeholder="00"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Add Inventory Button */}
          <div className="bg-white pt-4 px-6">
            <button
              type="submit"
              disabled={addStockLoading || !isFormValid()}
              className={`w-full py-3 font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                addStockLoading || !isFormValid()
                  ? "bg-[#ABB6AC] text-white cursor-not-allowed"
                  : "bg-[#2B8C34] text-white hover:bg-[#1e6326] focus:ring-[#2B8C34]"
              }`}
            >
              {addStockLoading ? "Adding Stock..." : "Add Stock"}
            </button>
          </div>
        </form>
      )}

      {/* Create Stock Request */}
      {addStockMode === "request" && (
        <div className="border-t border-[#E1E6E1]">
          {/* Product Name */}
          <div className="bg-white w-full flex">
            <div className="bg-[#F6F7F6] border-l border-b border-[#E1E6E1] px-6 py-2 w-1/4 ">
              <p className="text-xs text-[#5F6D60] font-semibold">
                Product Name
              </p>
            </div>
            <div className="border-b border-x border-[#E1E6E1] bg-white items-center w-3/4 flex gap-1 px-2 py-2">
              <p className="text-xs text-[#5F6D60] font-semibold border border-[#E1E6E1] rounded-[4px] px-1 py-0.5 w-fit">
                {product.product_name}
              </p>{" "}
              <span className="text-xs text-[#5F6D60] font-semibold">/</span>
              <p className="text-xs text-[#5F6D60] font-semibold border border-[#E1E6E1] rounded-[4px] px-1 py-0.5 w-fit">
                {product?.variant_data?.type}
              </p>{" "}
              <span className="text-xs text-[#5F6D60] font-semibold">/</span>
              <p className="text-xs text-[#5F6D60] font-semibold border border-[#E1E6E1] rounded-[4px] px-1 py-0.5 w-fit">
                {product?.variant_data?.value}
                {product?.variant_data?.unit}
              </p>{" "}
            </div>
          </div>

          {/* Current Stock */}
          <div className="bg-white w-full flex">
            <div className="bg-[#F6F7F6] border-l border-b border-[#E1E6E1] px-6 py-2 w-1/4 ">
              <p className="text-xs text-[#5F6D60] font-semibold">
                Current Stock
              </p>
            </div>
            <div className="border-b border-x border-[#E1E6E1] bg-white items-center w-3/4 flex gap-1 px-2 py-2">
              <p className="text-xs text-[#D4A407] font-semibold border border-[#D4A407] rounded-[4px] px-1 py-0.5 w-fit">
                {totalCurrentQuantity}
              </p>{" "}
            </div>
          </div>

          <div className="bg-white w-full flex">
            <div className="bg-[#F6F7F6] border-l border-b border-[#E1E6E1] px-6 py-2 w-full ">
              <p className="text-sm text-[#3E473F] font-semibold">
                Request Info
              </p>
            </div>
          </div>

          {/* Request Quantity */}
          <div className="bg-white pt-4 px-6">
            <div className="border border-[#E1E6E1] bg-[#F6F7F6] px-4 py-2 rounded-tl-[8px] rounded-tr-[8px]">
              <p className="text-sm text-[#3E473F] font-semibold">
                Request Quantity
              </p>
            </div>
            <div className="px-4 py-3 border border-[#E1E6E1] bg-white rounded-bl-[8px] rounded-br-[8px]">
              <input
                type="text"
                value={stockRequestData.requestQuantity}
                onChange={(e) =>
                  setStockRequestData({
                    ...stockRequestData,
                    requestQuantity: e.target.value,
                  })
                }
                className="w-full text-2xl font-semibold text-[#3E473F] bg-transparent border-none focus:outline-none"
                placeholder="00"
              />
            </div>
          </div>

          {/* Select store */}
          {hasPermission(user, PRIVILEGES.REQUEST_NEW_STOCK) &&
            user.department !== "STORE" && (
              <div className="pt-4 px-6">
                <div className="relative">
                  <p className="text-sm font-semibold text-[#3E473F] mb-1">
                    Select Store
                  </p>
                  <div
                    className={`flex items-center cursor-pointer justify-between outline-0 h-[44px] bg-white rounded-lg border border-[#E1E6E1] px-3 w-full font-medium text-sm leading-5 ${
                      allStoreLocationsLoading
                        ? "opacity-50 cursor-not-allowed"
                        : ""
                    }`}
                    onClick={() =>
                      !allStoreLocationsLoading &&
                      setRequestStoreToggle(!isRequestStoreToggle)
                    }
                  >
                    {selectedRequestStore ? (
                      <span className="font-semibold text-sm text-[#344335]">
                        {selectedRequestStore.name}
                      </span>
                    ) : (
                      <span className="text-[#344335]">
                        {allStoreLocationsLoading
                          ? "Loading stores..."
                          : "Select store"}
                      </span>
                    )}
                    {isRequestStoreToggle ? (
                      <svg
                        className="w-4 h-4 text-[#ABB6AC]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-4 h-4 text-[#ABB6AC]"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    )}
                  </div>
                  {isRequestStoreToggle && (
                    <div
                      className="absolute mt-2 border rounded-lg p-2 w-full bg-white z-20 max-h-[250px] overflow-y-scroll"
                      style={{
                        boxShadow:
                          "0px 16px 32px 0px #00000014, 0px 0px 1px 0px #00000040",
                      }}
                    >
                      <input
                        type="text"
                        placeholder="Search stores..."
                        value={requestStoreSearch}
                        onChange={(e) => setRequestStoreSearch(e.target.value)}
                        className="w-full mb-2 px-2 py-2 border border-[#E1E6E1] rounded text-sm"
                      />
                      {filteredRequestStores.length === 0 ? (
                        <div className="flex justify-center items-center h-[60px]">
                          <p className="text-[#96A397] text-sm">
                            No stores found
                          </p>
                        </div>
                      ) : (
                        filteredRequestStores.map((store) => (
                          <div
                            onClick={() => handleRequestStoreSelect(store)}
                            key={store.id}
                            className={`flex items-center gap-3 px-4 py-2 hover:bg-[#F6F7F6] hover:rounded cursor-pointer ${
                              selectedRequestStore?.id === store.id
                                ? "bg-[#EDF7EE] rounded"
                                : ""
                            }`}
                          >
                            <div className="flex-1">
                              <div className="font-semibold text-sm text-[#3E473F]">
                                {store.name}
                              </div>
                              <div className="text-[#3E473F] text-xs font-medium">
                                {store.address || ""}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* Request Note */}
          <div className="bg-white pt-4 px-6">
            <div className="border border-[#E1E6E1] bg-[#F6F7F6] px-4 py-2 rounded-tl-[8px] rounded-tr-[8px]">
              <p className="text-sm text-[#3E473F] font-semibold">
                Request Note (optional)
              </p>
            </div>
            <div className="px-4 py-3 border border-[#E1E6E1] bg-white rounded-bl-[8px] rounded-br-[8px]">
              <textarea
                value={stockRequestData.requestNote}
                onChange={(e) =>
                  setStockRequestData({
                    ...stockRequestData,
                    requestNote: e.target.value,
                  })
                }
                className="w-full h-24 text-sm font-semibold text-[#3E473F] bg-transparent border-none focus:outline-none placeholder:text-sm placeholder:text-gray-400"
                placeholder="What do you want the Sales Admin to know"
              />
            </div>
          </div>

          <div className="bg-white pt-4 px-6">
            <button
              type="submit"
              onClick={handleMakeRequest}
              disabled={
                createStockRequestLoading ||
                !stockRequestData.requestQuantity ||
                Number(stockRequestData.requestQuantity) <= 0
              }
              className={`w-full py-3 font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                createStockRequestLoading ||
                !stockRequestData.requestQuantity ||
                Number(stockRequestData.requestQuantity) <= 0
                  ? "bg-[#ABB6AC] text-white cursor-not-allowed"
                  : "bg-[#2B8C34] text-white hover:bg-[#1e6326] focus:ring-[#2B8C34]"
              }`}
            >
              {createStockRequestLoading ? "Making Request..." : "Make Request"}
            </button>
          </div>
        </div>
      )}
    </DrawerComponent>
  );
};

export default AddInventoryDrawer;
